<script setup lang="ts">
import type { BiomarkerQuantitativeData } from '@src/store/analysis/biomarker-quant-data';
import type { BiomarkerType } from '@src/store/analysis/data-typing';

import type { TableData } from './AnalysisTable.vue';

import { OCGTerritories } from '@src/store/analysis/data-typing';
import { computed, ref, watch } from 'vue';

import { useAnalysisStore } from '@/src/store/analysis';
import { BiomarkerManager } from '@/src/store/analysis/biomarker-manager-class';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import AnalysisTable from './AnalysisTable.vue';

const props = defineProps<{
  viewerID: string;
}>();

const analysisStore = useAnalysisStore();
const biomarkerContainer = computed(() =>
  analysisStore.getBiomarkerContainer(),
);

const viewportStateStore = useViewportStateStore();
const viewerIndx = computed(() => {
  return viewportStateStore.getSelectedImageIndices(props.viewerID);
});

const seriesUID = computed(() => {
  if (
    viewerIndx.value.seriesIdx === null
    || viewerIndx.value.seriesIdx === undefined
  ) {
    return '';
  }
  const currentStudyStore = useCurrentStudyStore();
  return currentStudyStore.getSeriesUID(viewerIndx.value.seriesIdx);
});

type ViewType = 'all' | 'regional' | 'stress' | 'recovery' | 'other';
const activeView = ref<ViewType>('all');

// --- Quantitative Data ---

const sliceQuantData = computed(() => {
  if (
    viewerIndx.value.sliceIdx === null
    || viewerIndx.value.sliceIdx === undefined
    || !seriesUID.value
  ) {
    return [];
  }
  return (
    biomarkerContainer.value?.getQuantitativeDataBySlice(
      seriesUID.value,
      viewerIndx.value.sliceIdx,
    ) || []
  );
});

function buildQuantTableData(
  title: string,
  items: BiomarkerQuantitativeData[],
): TableData {
  const rows = items.map((item) => {
    const rawName = item.getBiomarkerName();
    const label
      = biomarkerContainer.value?.getLabelFromBiomarkerType(rawName) || rawName;
    const value = item.getFormattedValue(2);
    const normalRange
      = biomarkerContainer.value?.getBiomarkerNormalRangeLabel(rawName) || '-';
    return [label, value, normalRange];
  });

  return {
    title,
    headers: ['Biomarker', 'Value', 'Normal Range*'],
    rows,
  };
}

// Stress Biomarkers Table
const stressTableData = computed<TableData>(() => {
  const items = sliceQuantData.value.filter(
    item => BiomarkerManager.getBiomarkerGroup(item.getBiomarkerName()) === 'stress',
  );
  return buildQuantTableData('Stress Biomarkers', items);
});

// Recovery Biomarkers Table
const recoveryTableData = computed<TableData>(() => {
  const items = sliceQuantData.value.filter(
    item => BiomarkerManager.getBiomarkerGroup(item.getBiomarkerName()) === 'recovery',
  );
  return buildQuantTableData('Recovery Biomarkers', items);
});

// Other Biomarkers Table (excludes regional markers to prevent duplicate display)
const otherTableData = computed<TableData>(() => {
  const items = sliceQuantData.value.filter(
    item => BiomarkerManager.getBiomarkerGroup(item.getBiomarkerName()) === 'other',
  );
  return buildQuantTableData('Other Biomarkers', items);
});

// --- Regional AHA Data ---

const REGIONAL_CANDIDATES: BiomarkerType[] = [
  'ocgDiaMaxIntensity',
  'ocgEntropy',
  'BMOREMax',
  'BMORE',
  'TemporalOCGMedian',
  'Vasoconstriction',
];

const availableRegionalBiomarkers = computed<BiomarkerType[]>(() => {
  if (
    viewerIndx.value.sliceIdx === null
    || viewerIndx.value.sliceIdx === undefined
    || !seriesUID.value
  ) {
    return [];
  }
  const sliceIndex = viewerIndx.value.sliceIdx;
  const avail
    = biomarkerContainer.value?.getAvailableAHABiomarkerTypes(
      seriesUID.value,
      sliceIndex,
    ) || [];

  const matched = REGIONAL_CANDIDATES.filter(b => avail.includes(b));
  if (matched.length > 0) {
    return matched;
  }

  return REGIONAL_CANDIDATES.filter((b) => {
    const data = biomarkerContainer.value?.getAHADataBySliceIndexAndType(
      seriesUID.value,
      sliceIndex,
      b,
    );
    return data && data.length > 0;
  });
});

const getBiomarker = (biomarker: BiomarkerType) => {
  if (
    viewerIndx.value.sliceIdx === null
    || viewerIndx.value.sliceIdx === undefined
    || !seriesUID.value
  ) {
    return null;
  }

  const biomarkerData = biomarkerContainer.value?.getAHADataBySliceIndexAndType(
    seriesUID.value,
    viewerIndx.value.sliceIdx,
    biomarker,
  );

  if (!biomarkerData || !biomarkerData[0]) {
    return null;
  }
  return biomarkerData[0];
};

const getBiomarkerNormalRange = (biomarker: BiomarkerType) => {
  const range
    = biomarkerContainer.value?.getBiomarkerNormalRangeLabel(biomarker);

  if (!range) {
    return '-';
  }

  return range;
};

const formatValue = (value: number | null) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '-'
    : value.toFixed(2);

const regionalTableData = computed<TableData>(() => {
  const rows = [];
  const biomarkers = availableRegionalBiomarkers.value;
  for (let i = 0; i < biomarkers.length; i++) {
    const biomarker = biomarkers[i];
    const biomarkerLabel
      = biomarkerContainer.value?.getLabelFromBiomarkerType(biomarker)
        || biomarker;
    const biomarkerData = getBiomarker(biomarker);
    const row: string[] = [];
    for (let j = 0; j < OCGTerritories.length; j++) {
      const territory = OCGTerritories[j];
      const data = biomarkerData?.getTerritoryMedian(territory);

      if (data === undefined) {
        row.push('-');
        continue;
      }
      const { result, std } = data;
      row.push(`${formatValue(result)} ± ${formatValue(std)}`);
    }
    const normalRange = getBiomarkerNormalRange(biomarker);
    rows.push([biomarkerLabel, ...row, normalRange]);
  }

  return {
    title: 'Regional Biomarkers',
    headers: ['Biomarker', ...OCGTerritories, 'Normal Range*'],
    rows,
  };
});

// --- Counts and Visibility ---

const regionalCount = computed(() => regionalTableData.value.rows.length);
const stressCount = computed(() => stressTableData.value.rows.length);
const recoveryCount = computed(() => recoveryTableData.value.rows.length);
const otherCount = computed(() => otherTableData.value.rows.length);

const totalAvailableSections = computed(() => {
  let count = 0;
  if (regionalCount.value > 0) count++;
  if (stressCount.value > 0) count++;
  if (recoveryCount.value > 0) count++;
  if (otherCount.value > 0) count++;
  return count;
});

const hasAnyData = computed(() => {
  return (
    regionalCount.value > 0
    || stressCount.value > 0
    || recoveryCount.value > 0
    || otherCount.value > 0
  );
});

// Reset to 'all' if selected tab no longer has data on slice switch
watch(
  [regionalCount, stressCount, recoveryCount, otherCount],
  () => {
    if (activeView.value === 'regional' && regionalCount.value === 0) {
      activeView.value = 'all';
    }
    else if (activeView.value === 'stress' && stressCount.value === 0) {
      activeView.value = 'all';
    }
    else if (activeView.value === 'recovery' && recoveryCount.value === 0) {
      activeView.value = 'all';
    }
    else if (activeView.value === 'other' && otherCount.value === 0) {
      activeView.value = 'all';
    }
  },
);
</script>

<template>
  <div class="biomarker-values-container">
    <!-- Tabs Header -->
    <div
      v-if="totalAvailableSections > 1"
      class="tabs-header mb-2"
    >
      <div class="tabs-navigation">
        <button
          type="button"
          class="tab-btn"
          :class="{ active: activeView === 'all' }"
          @click="activeView = 'all'"
        >
          All
        </button>
        <button
          v-if="regionalCount > 0"
          type="button"
          class="tab-btn"
          :class="{ active: activeView === 'regional' }"
          @click="activeView = 'regional'"
        >
          Regional ({{ regionalCount }})
        </button>
        <button
          v-if="stressCount > 0"
          type="button"
          class="tab-btn"
          :class="{ active: activeView === 'stress' }"
          @click="activeView = 'stress'"
        >
          Stress ({{ stressCount }})
        </button>
        <button
          v-if="recoveryCount > 0"
          type="button"
          class="tab-btn"
          :class="{ active: activeView === 'recovery' }"
          @click="activeView = 'recovery'"
        >
          Recovery ({{ recoveryCount }})
        </button>
        <button
          v-if="otherCount > 0"
          type="button"
          class="tab-btn"
          :class="{ active: activeView === 'other' }"
          @click="activeView = 'other'"
        >
          Other ({{ otherCount }})
        </button>
      </div>
    </div>

    <!-- 1. Regional Biomarkers Table (First) -->
    <div
      v-if="
        (activeView === 'all' || activeView === 'regional')
          && regionalTableData.rows.length > 0
      "
      class="table-section mb-3"
    >
      <div
        v-if="activeView === 'all' && totalAvailableSections > 1"
        class="section-title"
      >
        Regional Biomarkers (Territory)
      </div>
      <AnalysisTable :data="regionalTableData" />
    </div>

    <!-- 2. Stress Biomarkers Table (Second) -->
    <div
      v-if="
        (activeView === 'all' || activeView === 'stress')
          && stressTableData.rows.length > 0
      "
      class="table-section mb-3"
    >
      <div
        v-if="activeView === 'all' && totalAvailableSections > 1"
        class="section-title"
      >
        Stress Biomarkers
      </div>
      <AnalysisTable :data="stressTableData" />
    </div>

    <!-- 3. Recovery Biomarkers Table (Third) -->
    <div
      v-if="
        (activeView === 'all' || activeView === 'recovery')
          && recoveryTableData.rows.length > 0
      "
      class="table-section mb-3"
    >
      <div
        v-if="activeView === 'all' && totalAvailableSections > 1"
        class="section-title"
      >
        Recovery Biomarkers
      </div>
      <AnalysisTable :data="recoveryTableData" />
    </div>

    <!-- 4. Other Biomarkers Table (Fourth) -->
    <div
      v-if="
        (activeView === 'all' || activeView === 'other')
          && otherTableData.rows.length > 0
      "
      class="table-section mb-3"
    >
      <div
        v-if="activeView === 'all' && totalAvailableSections > 1"
        class="section-title"
      >
        Other Biomarkers
      </div>
      <AnalysisTable :data="otherTableData" />
    </div>

    <!-- Empty State -->
    <div
      v-if="!hasAnyData"
      class="no-data"
    >
      No biomarker data available for this slice.
    </div>

    <!-- Footnote -->
    <p
      v-if="hasAnyData"
      class="normal-range-note"
    >
      *Normal ranges are subject to change as we continue validating our
      biomarkers against larger datasets.
    </p>
  </div>
</template>

<style scoped>
.biomarker-values-container {
  width: 100%;
}

.tabs-header {
  background-color: var(--md-surface-container);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 6px;
  margin-bottom: 8px;
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
}

.tabs-navigation {
  display: flex;
  gap: 4px;
}

.tab-btn {
  background: transparent;
  border: none;
  color: var(--md-on-surface-variant);
  font-size: 0.8rem;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
}

.tab-btn:hover {
  color: var(--md-on-surface);
  background-color: var(--md-surface-container-high);
}

.tab-btn.active {
  color: #fff;
  background-color: var(--md-surface-container-highest);
  font-weight: 600;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--md-on-surface);
  margin-bottom: 4px;
  margin-top: 2px;
}

.table-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.no-data {
  font-size: 0.85rem;
  color: var(--md-on-surface-variant);
  font-style: italic;
  padding: 8px 0;
}

.normal-range-note {
  margin-top: 0.5rem;
  margin-bottom: 0;
  font-size: 0.75rem;
  font-style: italic;
  color: var(--md-on-surface-variant);
}
</style>
